import { line, max, scaleBand, scaleLinear, scaleOrdinal, select } from 'd3';
import React from 'react';
import ReactFauxDOM from 'react-faux-dom';
import { connect } from 'react-redux';
import DrilldownList from '../ObjFields/DrilldownList';
import { ObjGraphsRadarContainer } from './ObjGraphsRadarCSS';
import { lighten } from '../../../Utils/colorManipulator';

class ObjGraphsRadar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mode: 'graph', // 'graph' | 'list'
      drillCat1: null, // axe (cat1)
      drillCat2: null, // série (cat2)
    };
  }

  handleBack = () =>
    this.setState({ mode: 'graph', drillCat1: null, drillCat2: null });

  // options JSON -> meta map { rawValue: {display, color, icon} }
  toMetaMap = (options) => {
    try {
      const arr = typeof options === 'string' ? JSON.parse(options) : options;
      if (!Array.isArray(arr)) return {};
      return arr.reduce((acc, o) => {
        acc[o.value] = {
          display: o.display ?? o.value,
          color: o.color,
          icon: o.icon,
        };
        return acc;
      }, {});
    } catch {
      return {};
    }
  };

  renderLegend = (cats, cat2Meta) => {
    const badgeStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '2px 8px',
      borderRadius: 5,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '16px',
      verticalAlign: 'middle',
      boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
      cursor: 'pointer',
      userSelect: 'none',
      border: '1px solid rgba(0,0,0,0.06)',
      color: 'var(--ds-text-base)',
    };
    const legendWrap = {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginBottom: 8,
    };

    return (
      <div style={legendWrap}>
        {cats.map((raw) => {
          const meta = cat2Meta[raw] || {};
          const bg = lighten(meta.color, 0.9) || 'var(--ds-bg-secondary)';
          return (
            <span
              key={`legend-${raw}`}
              style={{
                ...badgeStyle,
                background: bg,
                color: 'var(--ds-text-base)',
              }}
              title={`Voir détails • ${
                this.props.cat2Label || this.props.formulaCat2 || 'Cat2'
              }: ${meta.display || raw}`}
              onClick={() =>
                this.setState({
                  mode: 'list',
                  drillCat1: null,
                  drillCat2: raw /* RAW */,
                })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  this.setState({
                    mode: 'list',
                    drillCat1: null,
                    drillCat2: raw /* RAW */,
                  });
                }
              }}
              tabIndex={0}
              role="button"
            >
              {meta.icon && (
                <span
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: '#000',
                    mask: `url('${meta.icon}') no-repeat center/14px 14px`,
                  }}
                />
              )}
              {meta.display || String(raw)}
            </span>
          );
        })}
      </div>
    );
  };

  render() {
    const { mode, drillCat1, drillCat2 } = this.state;
    const {
      pal,
      optionsCat1,
      optionsCat2,
      data: rawData = [],
      mini,
      objId,
      formulaCat1,
      formulaCat2,
      extraCondition,
      profiles,
      cat1Meta: cat1MetaProp,
      cat2Meta: cat2MetaProp,
      cat1Label = formulaCat1,
      cat2Label = formulaCat2,
      title,
    } = this.props;

    const cat1Meta = cat1MetaProp || this.toMetaMap(optionsCat1);
    const cat2Meta = cat2MetaProp || this.toMetaMap(optionsCat2);

    if (mode === 'list') {
      return (
        <ObjGraphsRadarContainer
          $transform={mini ? 'scale(1,1)' : ''}
          $height={mini ? '150px' : '200px'}
        >
          <DrilldownList
            title={title}
            objId={objId}
            formulaCat1={formulaCat1}
            formulaCat2={formulaCat2}
            cat1={drillCat1} // RAW
            cat2={drillCat2} // RAW
            extraCondition={extraCondition}
            profiles={profiles}
            onBack={this.handleBack}
            cat1Meta={cat1Meta}
            cat2Meta={cat2Meta}
            cat1Label={cat1Label}
            cat2Label={cat2Label}
          />
        </ObjGraphsRadarContainer>
      );
    }

    const isDark = pal.isDark;
    const width = mini ? 130 : 180;
    const widthGfx = width * 0.7;
    const height = mini ? 70 : 100;

    let optionsArray;
    try {
      optionsArray = JSON.parse(optionsCat1);
    } catch (e) {}

    const svg = select(ReactFauxDOM.createElement('g'));
    rawData.reduce((s, e) => s + parseInt(e.result, 10), 0); // sum non utilisé ici

    const x = scaleBand()
      .rangeRound([0, widthGfx])
      .paddingInner(0.5)
      .align(0.1);
    const y = scaleLinear().rangeRound([height, 0]);
    const z = scaleOrdinal().range([
      '#98abc5',
      '#8a89a6',
      '#7b6888',
      '#6b486b',
      '#a05d56',
      '#d0743c',
      '#ff8c00',
    ]);

    // === Reformattage des données (comme ton code d’origine) ===
    let data = rawData;
    const myArray = [];
    const keys = []; // RAW values of cat1
    const cats = []; // RAW values of cat2
    for (const row of data) {
      const key = Object.keys(row)[1];
      const cat = Object.keys(row)[2];
      if (!cats.includes(row[cat])) cats.push(row[cat]);
      if (!keys.includes(row[key])) keys.push(row[key]);
      let obj = myArray.find((it) => it[key] === row[key]);
      if (!obj) {
        obj = { [key]: row[key] };
        myArray.push(obj);
      }
      obj[row[Object.keys(row)[2]]] = row[Object.keys(row)[0]];
    }
    data = myArray;

    x.domain(keys);
    y.domain([
      0,
      max(data, (d) => cats.reduce((t, c) => t + (+d[c] || 0), 0)),
    ]).nice();
    z.domain(cats);

    const maxInd = 10.0;
    const summ = data.map((obj) =>
      Number(
        Object.keys(obj).reduce(
          (a, c) => (isNaN(obj[c]) ? a : a + Number(obj[c])),
          0
        )
      )
    );
    const ratio = maxInd / summ.reduce((m, c) => Math.max(m, c), 0);
    const rotate = 360.0 / data.length;

    const sumObj = (d) =>
      Number(
        Object.keys(d).reduce(
          (a, c) => (isNaN(d[c]) ? a : a + Number(d[c])),
          0
        ) *
          5 +
          0
      );
    const starFunction = line()
      .y(
        (d, ind) =>
          Math.cos((rotate / 2) * ind * (Math.PI / 180)) * ratio * sumObj(d)
      )
      .x(
        (d, ind) =>
          Math.sin((rotate / 2) * ind * (Math.PI / 180)) * ratio * sumObj(d)
      );
    const lineFunction = line()
      .y(
        (d, ind) =>
          Math.cos(rotate * (ind + 1) * (Math.PI / 180)) * ratio * sumObj(d)
      )
      .x(
        (d, ind) =>
          Math.sin(rotate * (ind + 1) * (Math.PI / 180)) * ratio * sumObj(d)
      );

    const g = svg
      .append('g')
      .attr('transform', mini ? 'translate(35,5)' : 'translate(60,20)');
    g.selectAll('g');

    // ombres
    const filter = g.append('defs').append('filter').attr('id', 'dropShadow');
    filter
      .append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', 0.6)
      .attr('class', 'blur');
    filter.append('feOffset').attr('dx', 0.3).attr('dy', 0.3);
    const fm = filter.append('feMerge');
    fm.append('feMergeNode').attr('in', 'SourceGraphic');
    filter
      .append('feComponentTransfer')
      .append('feFuncA')
      .attr('type', 'linear')
      .attr('slope', 0.3);

    // grille
    const mkContour = (valRatio) => [
      { a: valRatio },
      { b: valRatio },
      { c: valRatio },
      { d: valRatio },
      { e: valRatio },
      { a: valRatio },
      { b: valRatio },
      { c: valRatio },
      { d: valRatio },
      { e: valRatio },
    ];

    const radar = svg
      .append('g')
      .attr(
        'transform',
        mini
          ? 'scale(1.0,1.0) translate(100,55)'
          : 'scale(1.2,1.2) translate(80,55)'
      );
    const opacityLine = 1;

    radar
      .append('path')
      .attr('transform', 'scale(0.8,0.8) translate(0,0)')
      .attr('d', `${lineFunction(mkContour(2.0 / ratio))}Z`)
      .attr('stroke', isDark ? 'white' : '#AAAAAA')
      .attr('stroke-width', 0.5)
      .attr('stroke-opacity', opacityLine)
      .attr('fill-opacity', 0)
      .attr('fill', isDark ? 'white' : '#666666');

    radar
      .append('path')
      .attr('transform', 'scale(1,1) translate(0,0)')
      .attr('d', `${lineFunction(mkContour(10.0 / ratio))}Z`)
      .attr('stroke', isDark ? 'white' : '#AAAAAA')
      .attr('stroke-width', 0.5)
      .attr('stroke-opacity', opacityLine)
      .attr('fill-opacity', 0.8)
      .attr('fill', 'white');

    radar
      .append('path')
      .attr('transform', 'scale(1,1) translate(0,0)')
      .attr('d', `${lineFunction(mkContour(6.0 / ratio))}Z`)
      .attr('stroke', isDark ? 'white' : '#AAAAAA')
      .attr('stroke-width', 0.5)
      .attr('stroke-opacity', opacityLine)
      .attr('fill-opacity', 0)
      .attr('fill', isDark ? 'white' : '#666666');

    radar
      .append('path')
      .attr('transform', 'scale(1,1) translate(0,0)')
      .attr('d', `${lineFunction(mkContour(2.0 / ratio))}Z`)
      .attr('stroke', isDark ? 'white' : '#AAAAAA')
      .attr('stroke-width', 0.5)
      .attr('stroke-opacity', opacityLine)
      .attr('fill-opacity', 0)
      .attr('fill', isDark ? 'white' : '#666666');

    // étoile
    const starContour = [
      { a: 10.0 / ratio },
      { b: 0 },
      { c: 10.0 / ratio },
      { d: 0 },
      { e: 10.0 / ratio },
      { a: 0 },
      { b: 10.0 / ratio },
      { c: 0 },
      { d: 10.0 / ratio },
      { e: 0 },
      { d: 10.0 / ratio },
      { e: 0 },
      { d: 10.0 / ratio },
      { e: 0 },
      { d: 10.0 / ratio },
      { e: 0 },
    ];
    radar
      .append('path')
      .attr('transform', 'scale(1,1) translate(0,0)')
      .attr('d', `${starFunction(starContour)}Z`)
      .attr('stroke', isDark ? 'white' : '#AAAAAA')
      .attr('stroke-width', 1)
      .attr('stroke-opacity', 1)
      .attr('fill-opacity', 0.8)
      .attr('fill', isDark ? 'white' : '#CCCCCC');

    // polygone principal
    radar
      .append('path')
      .attr('transform', 'scale(1,1) translate(0,0)')
      .attr('d', `${lineFunction(data)}Z`)
      .attr('stroke', '#1C7ED6')
      .attr('stroke-linecap', 'round')
      .attr('stroke-width', 1)
      .attr('stroke-opacity', 1)
      .attr('fill-opacity', isDark ? '0.6' : '0.3')
      .attr('fill', isDark ? 'white' : '#1C7ED6');

    // calcule le total d'une clé
    const getTotalForKey = (keyValue) => {
      const obj = data.find((d) => d[Object.keys(d)[0]] === keyValue);
      if (!obj) return 0;
      return cats.reduce((sum, cat) => sum + (+obj[cat] || 0), 0);
    };

    // on lie RAW + DISPLAY + COLOR + ICON
    let boundKeys = keys
      .slice()
      .reverse()
      .map((raw) => {
        const meta = cat1Meta[raw] || {};
        return {
          raw,
          display: meta.display ?? raw,
          color: meta.color,
          icon: meta.icon,
          total: getTotalForKey(raw),
        };
      });

    const self = this;

    // boundKeys = boundKeys.sort((a, b) => a.display.localeCompare(b.display));
    // helpers badge (mêmes “valeurs” que la légende)
    const PAD_X = 8;
    const PAD_Y = 2;
    const GAP = 6;
    const ICON = 14;
    const FONT_SIZE = 9;
    const FONT_WEIGHT = 600;
    const BORDER = '1px solid rgba(0,0,0,0.06)';
    const RADIUS = 5;

    // utilitaire pour mesurer la largeur d'un texte
    function measureTextWidth(
      text,
      font = `${FONT_WEIGHT} ${FONT_SIZE}px sans-serif`
    ) {
      if (typeof document === 'undefined') return text.length * FONT_SIZE * 0.6; // fallback SSR
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.font = font;
      return ctx.measureText(text).width;
    }

    // positionnement polaire (mêmes formules que tes <text>)
    const centerX = (i) => 70 * Math.cos(rotate * (i + 1) * (Math.PI / 180));
    const centerY = (i) =>
      50 * Math.sin(rotate * (i + 1) * (Math.PI / 180)) + 20;

    // conteneur des badges
    const badges = radar
      .selectAll('g.badge')
      .data(boundKeys)
      .enter()
      .append('g')
      .attr('class', 'badge')
      .attr('transform', (_d, i) => `translate(${centerX(i)},${centerY(i)})`)
      .style('cursor', 'pointer')
      .attr('role', 'button')
      .attr('tabindex', 0)
      .on('click', (_evt, d) => {
        self.setState({ mode: 'list', drillCat1: d.raw, drillCat2: null });
      })
      .on('keydown', (evt, d) => {
        if (evt.key === 'Enter' || evt.key === ' ') {
          evt.preventDefault();
          self.setState({ mode: 'list', drillCat1: d.raw, drillCat2: null });
        }
      });

    // d’abord le texte (pour mesurer), puis on ajoute le rect aux bonnes dimensions
    const labelText = badges
      .append('text')
      .text((d) => `${d.display} (${d.total})`)
      .attr('font-size', FONT_SIZE)
      .attr('font-weight', FONT_WEIGHT)
      .attr('fill', 'var(--ds-text-base)')
      .style('dominant-baseline', 'middle') // centrage vertical
      .style('text-anchor', 'start'); // on calera x après mesure

    // defs pour les filtres d'icône
    const defs = radar.select('defs#icon-defs').empty()
      ? radar.append('defs').attr('id', 'icon-defs')
      : radar.select('defs#icon-defs');

    // util pour id safe
    const safeId = (s) => String(s ?? '').replace(/[^\w-]/g, '_');

    // mesure, puis dessin rect + (éventuelle) icône et repositionnement texte
    badges.each(function (d) {
      const g = select(this);
      const t = g.select('text').node();

      const hasIcon = Boolean(d.icon);
      const bg = lighten(d.color, 0.6) || 'var(--ds-bg-secondary)';

      const textContent = t?.textContent || '';
      const width = t?.getComputedTextLength
        ? t.getComputedTextLength()
        : measureTextWidth(textContent);

      const height = FONT_SIZE * 1.2;

      const contentW = (hasIcon ? ICON + GAP : 0) + width;
      const contentH = Math.max(height, hasIcon ? ICON : 0);

      const w = PAD_X * 2 + contentW;
      const h = PAD_Y * 2 + contentH;

      // fond badge
      g.insert('rect', 'text')
        .attr('x', -w / 2)
        .attr('y', -h / 2)
        .attr('width', w)
        .attr('height', h)
        .attr('rx', RADIUS)
        .attr('ry', RADIUS)
        .attr('fill', bg)
        .attr('stroke', 'rgba(0,0,0,0.06)')
        .attr('stroke-width', 1)
        .attr('filter', 'url(#dropShadow)');

      // icône en monochrome
      if (hasIcon) {
        const fid = `tint-${safeId(d.raw)}`;

        // filtre de teinte: colorie avec d.color en utilisant l'alpha de l'image
        const f = defs
          .append('filter')
          .attr('id', fid)
          .attr('color-interpolation-filters', 'sRGB');

        f.append('feFlood')
          .attr('flood-color', d.color || '#000')
          .attr('result', 'flood');

        f.append('feComposite')
          .attr('in', 'flood')
          .attr('in2', 'SourceAlpha')
          .attr('operator', 'in')
          .attr('result', 'mask');

        f.append('feMerge').append('feMergeNode').attr('in', 'mask');

        // on dessine l'image et on lui applique le filtre
        g.append('image')
          .attr('href', d.icon)
          .attr('width', ICON)
          .attr('height', ICON)
          .attr('x', -w / 2 + PAD_X)
          .attr('y', -ICON / 2)
          .attr('filter', `url(#${fid})`);
      }

      // texte repositionné à l'intérieur du badge
      g.select('text')
        .attr('x', -w / 2 + PAD_X + (hasIcon ? ICON + GAP : 0))
        .attr('y', 0);
    });

    // tooltip natif
    badges
      .append('title')
      .text((d) => `${cat1Label || 'Cat1'}: ${d.display} (${d.total})`);

    const labels = radar
      .selectAll('text')
      .data(boundKeys)
      .enter()
      .append('text')
      .attr('transform', 'translate(0,0)')
      .style('text-anchor', 'middle')
      .style('dominant-baseline', 'central')
      .attr('x', (_d, b) => 70 * Math.cos(rotate * (b + 1) * (Math.PI / 180)))
      .attr('y', (_d, b) => 50 * Math.sin(rotate * (b + 1) * (Math.PI / 180)))
      .attr('font-weight', 'bold')
      .attr('font-size', 11)
      .attr('cursor', 'pointer')
      .attr('role', 'button')
      .attr('tabindex', 0)
      .attr('fill', function (d) {
        return d.color ?? (isDark ? 'white' : '#548AE2');
      })
      .text((d) => {
        return `${d.display} (${d.total})`;
      });

    labels
      .append('title')
      .text((d) => `${cat1Label || 'Cat1'}: ${d.display} (${d.total})`);

    labels
      .on('click', function (d) {
        // IMPORTANT : envoyer d.raw (valeur brute) au drilldown
        self.setState({
          mode: 'list',
          drillCat1: d.raw,
          drillCat2: null,
        });
      })
      .on('keydown', function (evt, d) {
        if (evt.key === 'Enter' || evt.key === ' ') {
          evt.preventDefault();
          self.setState({
            mode: 'list',
            drillCat1: d.raw,
            drillCat2: null,
          });
        }
      });

    const res = svg.node().toReact();

    return (
      <ObjGraphsRadarContainer
        $transform={mini ? 'scale(1,1)' : ''}
        // $minHeight={mini ? '200px' : '200px'}
      >
        <svg>{res}</svg>
        {this.renderLegend(cats, cat2Meta)}
      </ObjGraphsRadarContainer>
    );
  }
}

export default connect(null, () => ({}))(ObjGraphsRadar);
